import { MintStatus } from '@arpeggi/shared'
import { format, secondsToMilliseconds } from 'date-fns'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { useMediaQuery } from '../../../hooks/useMediaQuery'
import { styled } from '../../../lib/stitches'
import { useArpMedia } from '../../../queries/arpMedia'
import { useMintStatus } from '../../../queries/mintStatuses'
import { useUser } from '../../../queries/users'
import { useTreeState } from '../../../stores/Root'
import { getMultipleSampleAudioBuffers } from '../../../stores/song/sample/SampleUtils'
import { deserializeSongFromStorage } from '../../../stores/song/SongStore'
import CenteredLoadingAnimation from '../../core/CenteredLoadingAnimation'
import { Box, Column, Row } from '../../core/Components'
import Text from '../../core/Text/Text'
import {
  PreviewContainer,
  PreviewContainerWrapper
} from '../../platform/explore/common/MediaPreview'
import { MintStatusFeedbackMap } from '../../platform/explore/common/MintInProgressPreview'
import { SongPlayer } from '../../player/Player'
import { formatTime } from '../../player/PlayerUtils'
import DetailPageDNA from '../shared/DetailPageDNA'
import { DetailPageTopSectionLayout } from '../shared/DetailPageTopSection'
import { MediaInfoTitle } from '../shared/MediaInfo'
import { SongInfoDisplay } from './SongInfo'

const StyledInfo = styled('div', {
  backgroundColor: '$gray02',
  borderRadius: '$8',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: '$gray04',
  padding: '$16'
})

const useSongPreviewPage = () => {
  const { jobId } = useParams()
  const navigate = useNavigate()
  const { player } = useTreeState()
  const [songLength, setSongLength] = useState('')
  const [isPlayerSongLoaded, setIsPlayerSongLoaded] = useState<boolean>(false)
  const [samples, setSamples] = useState<IArpMedia[]>([])
  const [songId, setSongId] = useState<number | undefined>(undefined)
  const [playerStatus, setPlayerStatus] = useState<'loading' | 'error' | 'ready'>('loading')
  const mintStatusQuery = useMintStatus({
    jobId,
    options: { refetchInterval: secondsToMilliseconds(30), retry: false }
  })
  const arpMediaQuery = useArpMedia({
    id: songId,
    params: { parts: ['subComponents'] },
    options: { enabled: songId !== undefined, refetchInterval: secondsToMilliseconds(10) }
  })
  const userQuery = useUser({
    walletAddressOrId: mintStatusQuery?.data?.user.walletAddress,
    options: { enabled: !!mintStatusQuery?.data?.user.walletAddress }
  })

  useEffect(() => {
    if (!userQuery.isLoading && !mintStatusQuery.isLoading && isPlayerSongLoaded) {
      setPlayerStatus('ready')
    }
  }, [mintStatusQuery?.isLoading, userQuery?.isLoading, isPlayerSongLoaded])

  useEffect(() => {
    if (
      mintStatusQuery.data?.mintStatus === MintStatus.Complete &&
      mintStatusQuery.data?.arpMediaId
    ) {
      // Need to poll on this song ID and redirect once its written to the DB
      setSongId(mintStatusQuery.data.arpMediaId)
    }
  }, [mintStatusQuery.data?.mintStatus, mintStatusQuery.data?.arpMediaId])

  useEffect(() => {
    if (arpMediaQuery.data?.id) {
      navigate(`/song?token=${arpMediaQuery.data.id}`, { replace: true })
    }
  }, [arpMediaQuery.data])

  useEffect(() => {
    setIsPlayerSongLoaded(false)
    const setup = async () => {
      const samples = await ArpRegistry.getByIdsAsync(
        mintStatusQuery.data.subComponents.map((sample) => sample.id)
      )
      await getMultipleSampleAudioBuffers(samples, 'core', player.getAudioEngine())
      setSamples(samples)

      const deserialized = deserializeSongFromStorage(mintStatusQuery.data.arpeggiComposition)
      setSongLength(formatTime(deserialized.lengthInSeconds))
      await player.initialize(false)
      await player.setSong(deserialized)
      player.enableAudioUpdates()
      setIsPlayerSongLoaded(true)
    }

    if (mintStatusQuery.data) {
      setup()
    }

    return () => {
      player.reset()
    }
  }, [mintStatusQuery.data?.arpeggiComposition])

  return {
    isError: mintStatusQuery.isError || userQuery.isError,
    user: userQuery.data,
    mintStatus: mintStatusQuery.data,
    player,
    playerStatus,
    songLength,
    samples
  }
}

const SongPreviewPage = () => {
  const { isError, user, mintStatus, player, playerStatus, songLength, samples } =
    useSongPreviewPage()
  const isLessThanTabletScreen = useMediaQuery(960)

  if (isError) {
    return <Navigate replace to="/home" />
  }

  return (
    <Column>
      <DetailPageTopSectionLayout
        showMoreInfo={false}
        color={player.song && player.song.tokenColor}
        columnLeft={
          <>
            {isLessThanTabletScreen && user && (
              <Box css={{ marginBottom: '$32' }}>
                <MediaInfoTitle
                  title={mintStatus?.metadata.title}
                  artistEthAddress={user.walletAddress}
                  artistName={user.displayName}
                />
              </Box>
            )}
            <PreviewContainerWrapper>
              <PreviewContainer>
                <SongPlayer
                  artist={{
                    avatar: user?.profilePicture?.url,
                    name: user?.displayName,
                    url: `/profile/${user?.walletAddress}`
                  }}
                  player={player}
                  status={playerStatus}
                  title={mintStatus?.metadata.title}
                  song={player.song}
                  bpm={mintStatus?.metadata.bpm}
                />
              </PreviewContainer>
            </PreviewContainerWrapper>
          </>
        }
        columnRight={
          !isLessThanTabletScreen && (
            <Row css={{ width: '100%', '@tablet': { flex: '1' } }}>
              {user ? (
                <SongInfoDisplay
                  title={mintStatus?.metadata.title}
                  artistEthAddress={user?.walletAddress}
                  artistName={user?.displayName}
                  tokenId={mintStatus?.tokenId}
                  contractAddress={mintStatus?.tokenContractAddress}
                  genre={mintStatus?.metadata.genres[0]}
                  mintDate={format(new Date(mintStatus.createdAt), 'MM/dd/yyy')}
                  songLength={songLength}
                />
              ) : (
                <CenteredLoadingAnimation />
              )}
            </Row>
          )
        }
      />
      <Box
        css={{
          paddingTop: '$32',
          paddingLeft: '15px',
          paddingRight: '15px',
          '@tablet': { paddingTop: '70px', paddingLeft: '140px', paddingRight: '140px' }
        }}
      >
        <StyledInfo>
          <Text size="16" css={{ marginBottom: '$8' }}>
            Your mint is still in progress. Full song DNA will be available when it's completed.
          </Text>
          <Text size="14">{MintStatusFeedbackMap[mintStatus?.mintStatus]}</Text>
        </StyledInfo>
      </Box>
      {samples.length > 0 && <DetailPageDNA title="Song" samplesUsed={samples} />}
    </Column>
  )
}

export default observer(SongPreviewPage)
